import { CheckIcon, Combobox, Group, Input, Pill, PillsInput, ScrollArea, Stack, Text, useCombobox } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useState } from "react";
import { OrganizationRolesRepository, UserSchema, UsersSearchRepository } from "shared/components/organization/users/data";
import { usePermissionCheck } from "shared/stores/oidc";
import { Permission, PermissionType } from "src/gql/graphql";
import { container } from "tsyringe";

const RolesCombobox = (props: {
    selectedRoles: UserSchema["roles"][0]["role"][];
    setSelectedRoles: React.Dispatch<React.SetStateAction<UserSchema["roles"][0]["role"][]>>;
}) => {
    const rolesCombobox = useCombobox({
        onDropdownClose: () => rolesCombobox.resetSelectedOption(),
        onDropdownOpen: () => rolesCombobox.updateSelectedOptionIndex(),
    });

    const loader = container.resolve(OrganizationRolesRepository).get();
    const roles = loader.useItems();

    const options = roles
        .filter((it) => it.type == PermissionType.Ipaws)
        .map((role) => {
            const active = props.selectedRoles.some((r) => r.id == role.id);
            return (
                <Combobox.Option value={role.id} key={role.id} active={active}>
                    <Group gap="sm">
                        {active && <CheckIcon size={12} />}
                        {role.name}
                    </Group>
                </Combobox.Option>
            );
        });

    function handleSelect(id: string) {
        const role = roles.find((r) => r.id == id);
        if (!role) return;
        props.setSelectedRoles((current) =>
            current.some((r) => r.id == role.id) ? current.filter((v) => v.id !== id) : [...current, role],
        );
    }

    function handleRemove(id: string) {
        props.setSelectedRoles((current) => current.filter((r) => r.id != id));
    }

    return (
        <Combobox onOptionSubmit={handleSelect} store={rolesCombobox} withinPortal={false}>
            <Combobox.DropdownTarget>
                <PillsInput onClick={() => rolesCombobox.openDropdown()} label="Role(s)">
                    <Pill.Group>
                        {props.selectedRoles.length > 0 ? (
                            props.selectedRoles.map((r) => (
                                <Pill key={r.id} withRemoveButton onRemove={() => handleRemove(r.id)}>
                                    {r.name}
                                </Pill>
                            ))
                        ) : (
                            <Input.Placeholder>Pick one or more roles</Input.Placeholder>
                        )}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                type="hidden"
                                onBlur={() => rolesCombobox.closeDropdown()}
                                onKeyDown={(event) => {
                                    if (event.key === "Backspace" && props.selectedRoles.length > 0) {
                                        event.preventDefault();
                                        handleRemove(props.selectedRoles.at(-1)!.id);
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>
            <Combobox.Dropdown>
                <Combobox.Options>
                    <ScrollArea.Autosize type="scroll" mah={200}>
                        {options.length === 0 ? <Combobox.Empty>No available roles</Combobox.Empty> : options}
                        <loader.LoadTrigger />
                    </ScrollArea.Autosize>
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};

const UsersCombobox = (props: { selectedUsers: UserSchema[]; setSelectedUsers: React.Dispatch<React.SetStateAction<UserSchema[]>> }) => {
    const [nameSearch, setNameSearch] = useState("");
    const [debouncedNameSearch] = useDebouncedValue(nameSearch, 400);

    const loader = container.resolve(UsersSearchRepository).get({ nameQuery: debouncedNameSearch ?? undefined });
    const users = loader.useItems();

    const usersCombobox = useCombobox({
        onDropdownClose: () => usersCombobox.resetSelectedOption(),
        onDropdownOpen: () => usersCombobox.updateSelectedOptionIndex("active"),
    });

    const options = users.map((user) => {
        const active = props.selectedUsers.some((u) => u.id == user.id);
        return (
            <Combobox.Option value={user.id} key={user.id} active={active}>
                <Group gap="sm">
                    {active && <CheckIcon size={12} />}
                    {user.name}
                    <Text fz="sm" fs="italic">
                        {user.loginEmail}
                    </Text>
                </Group>
            </Combobox.Option>
        );
    });

    function handleSelect(id: string) {
        const user = users.find((u) => u.id == id);
        if (!user) return;
        props.setSelectedUsers((current) =>
            current.some((u) => u.id == user.id) ? current.filter((v) => v.id !== id) : [...current, user],
        );
        setNameSearch("");
    }

    function handleRemove(id: string) {
        props.setSelectedUsers((current) => current.filter((r) => r.id != id));
    }

    return (
        <Combobox onOptionSubmit={handleSelect} store={usersCombobox} withinPortal={false}>
            <Combobox.DropdownTarget>
                <PillsInput onClick={() => usersCombobox.openDropdown()} label="User(s)">
                    <Pill.Group>
                        {props.selectedUsers.map((u) => (
                            <Pill key={u.id} withRemoveButton onRemove={() => handleRemove(u.id)}>
                                {u.name}
                            </Pill>
                        ))}

                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => usersCombobox.openDropdown()}
                                onBlur={() => usersCombobox.closeDropdown()}
                                value={nameSearch}
                                placeholder="Search users"
                                onChange={(event) => {
                                    usersCombobox.updateSelectedOptionIndex();
                                    setNameSearch(event.currentTarget.value);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Backspace" && nameSearch.length === 0 && props.selectedUsers.length > 0) {
                                        event.preventDefault();
                                        handleRemove(props.selectedUsers.at(-1)!.id);
                                    }
                                }}
                                autoComplete="off"
                            />
                        </Combobox.EventsTarget>
                    </Pill.Group>
                </PillsInput>
            </Combobox.DropdownTarget>
            <Combobox.Dropdown>
                <Combobox.Options>
                    <ScrollArea.Autosize type="scroll" mah={200}>
                        {options.length === 0 ? <Combobox.Empty>No available users</Combobox.Empty> : options}
                        <loader.LoadTrigger />
                    </ScrollArea.Autosize>
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};

export const CertificateRoleAssignPanel = ({
    usersProps,
    rolesProps,
}: {
    usersProps: {
        selectedUsers: UserSchema[];
        setSelectedUsers: React.Dispatch<React.SetStateAction<UserSchema[]>>;
    };
    rolesProps: {
        selectedRoles: UserSchema["roles"][0]["role"][];
        setSelectedRoles: React.Dispatch<React.SetStateAction<UserSchema["roles"][0]["role"][]>>;
    };
}) => {
    const hasRoleAssignPerm = usePermissionCheck(Permission.CustomerRolesManage);
    const hasGroupUsersView = usePermissionCheck(Permission.GroupUsersView);
    const hasTopicUsersView = usePermissionCheck(Permission.TopicUsersView);
    if (!hasRoleAssignPerm || !(hasGroupUsersView || hasTopicUsersView)) return null;

    return (
        <Stack pos="relative">
            <RolesCombobox {...rolesProps} />
            <UsersCombobox {...usersProps} />
        </Stack>
    );
};
